<template>
  <div class="help">
    <h1>📖 使用教程</h1>

    <el-alert
      type="info"
      :closable="false"
      show-icon
      title="采集监控依赖模拟器"
      description="本软件的「直播监控」「弹幕采集」等功能，需要配合 MuMu 模拟器（模拟器内登录抖音账号）才能运行。下方为完整使用说明。"
      style="margin-bottom: 16px"
    />

    <el-card style="margin-bottom: 16px">
      <template #header><b>① 下载安装 MuMu 模拟器</b></template>
      <ol class="steps">
        <li>打开 MuMu 模拟器官网：<el-link type="primary" href="https://www.mumu.163.com/" target="_blank">https://www.mumu.163.com/</el-link>（网易官方，认准官网，勿用第三方渠道）</li>
        <li>点击「立即下载」，下载 Windows 安装包（约 500MB）。</li>
        <li>双击安装包，按提示完成安装（建议安装在非系统盘，如 D 盘）。</li>
        <li>首次启动会初始化系统，等待进入模拟器桌面即可。</li>
      </ol>
    </el-card>

    <el-card style="margin-bottom: 16px">
      <template #header><b>② 模拟器内安装抖音并登录账号</b></template>
      <ol class="steps">
        <li>打开模拟器内置的「应用中心」（或浏览器访问 <el-link type="primary" href="https://www.douyin.com" target="_blank">douyin.com</el-link> 下载），搜索「抖音」并安装。</li>
        <li>打开抖音 App，使用<b>手机号+验证码</b>或<b>扫码</b>方式登录你的抖音账号。</li>
        <li>建议：一个模拟器实例登录一个账号，多账号用「模拟器多开」分别登录，避免账号关联风险。</li>
        <li>登录完成后，抖音 App 保持后台运行即可（采集时软件会自动打开直播间）。</li>
      </ol>
    </el-card>

    <el-card style="margin-bottom: 16px">
      <template #header><b>③ 开启模拟器 ADB 调试（本软件自动连接）</b></template>
      <ol class="steps">
        <li>打开 MuMu 模拟器右上角「设置」→「其他」→ 开启 <b>「ADB 调试」</b>（或「开发者选项」）。</li>
        <li>确认 ADB 端口为 <b>127.0.0.1:16384</b>（MuMu 12 默认端口；不同版本可能为 7555，可在模拟器设置中查看）。</li>
        <li>模拟器需保持<b>开机运行</b>状态，软件才能连接采集。</li>
        <li>电脑防火墙若拦截，请允许 MuMu 相关进程通过（家庭网络/专用网络都允许）。</li>
      </ol>
    </el-card>

    <el-card style="margin-bottom: 16px">
      <template #header><b>④ 软件内配置账号</b></template>
      <ol class="steps">
        <li>进入「账号管理」页，添加抖音账号（抖音号填写模拟器内登录的抖音号）。</li>
        <li>在「账号管理」中可点「查IP」查看该账号当前出口 IP，确认网络环境。</li>
        <li>进入「直播监控」页，配置要监控的直播间/博主，启动监控。</li>
        <li>采集到的弹幕会自动打标（意向识别），命中线索进入「线索库」。</li>
      </ol>
    </el-card>

    <el-card style="margin-bottom: 16px">
      <template #header><b>⑤ 常见问题</b></template>
      <el-collapse>
        <el-collapse-item title="为什么直播监控不采集数据？" name="1">
          <p>① 确认 MuMu 模拟器已开启且抖音已登录；② 确认模拟器 ADB 调试已开启（端口 16384）；③ 确认软件「系统设置」中的监控开关已开启；④ 检查模拟器内抖音是否停留在直播间页面。</p>
        </el-collapse-item>
        <el-collapse-item title="多个抖音账号如何避免关联封号？" name="2">
          <p>① 一个模拟器实例只登录一个账号；② 不同账号不要在同一网络出口下高频操作（可在「账号管理」查IP确认）；③ 操作间隔保持拟人化，避免短时间大量关注/私信。</p>
        </el-collapse-item>
        <el-collapse-item title="模拟器很卡怎么办？" name="3">
          <p>MuMu 模拟器「设置」→「性能」中适当调低分辨率与帧率；关闭不必要的后台应用；确保电脑内存 ≥ 8GB，建议 16GB（模拟器多开时更吃内存）。</p>
        </el-collapse-item>
        <el-collapse-item title="对标账号/视频采集需要模拟器吗？" name="4">
          <p>「对标分解」模块（对标账号作品采集/分析）走网页数据接口，<b>不需要</b>模拟器；但涉及直播间弹幕采集、真实观看、互动类任务时，需要模拟器配合。</p>
        </el-collapse-item>
      </el-collapse>
    </el-card>

    <el-card style="margin-bottom: 16px">
      <template #header><b>⑥ 评论区截流三招（合规教学版）</b></template>
      <ol class="steps">
        <li><b>抖音 @蓝字截流</b>：在同行高赞视频下留一条专业、有价值的评论（先帮到人），末尾 <b>@自己的小号</b>——发出后显示为蓝色可点击文字，感兴趣的人点一下直达你的主页。⚠️ 先给价值再引流，别硬广、别批量@，防平台风控。</li>
        <li><b>小红书 小蓝词截流</b>：把<b>品牌词/店名</b>提前埋进自己的笔记标题和内容；再到同行高赞笔记下，用另一个账号评论时带上这个词，配合互动让词变成"可点击搜索词"，用户点击直达你的品牌搜索结果。⚠️ 以真实内容为前提，勿批量刷词，否则账号易被限流。</li>
        <li><b>自动回复 蓝字链接</b>（公众号/企业微信）：客户加过来后，自动回复直接放<b>可点击的落地页/数字名片链接</b>，把"加好友→看介绍"缩短成一步。⚠️ 链接必须 http/https 开头；蓝字必须能跳转；勿频繁导流，避免触发风控封禁自动回复。</li>
      </ol>
      <p class="price-tips">提示：以上玩法均来自平台真实运营技巧，请以真实内容为前提、遵守各平台规则，量力而行。</p>
    </el-card>

    <el-card>
      <template #header><b>💰 找客点扣费标准（1 点 = 1 元）</b></template>
      <el-table :data="priceList" size="small" border>
        <el-table-column prop="name" label="功能" min-width="170" />
        <el-table-column prop="price" label="扣点标准" min-width="150" />
        <el-table-column prop="note" label="说明" min-width="180" />
      </el-table>
      <div class="price-tips">
        <p>· AI 对话每日前 10 次免费；TTS 语音合成、数字名片、监控任务免费</p>
        <p>· 点数类型：充值点（365天）｜体验点（30天）｜会员积分（当月）｜推广点（可提现）</p>
      </div>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'Help',
  data() {
    return {
      priceList: [
        { name: 'AI 视频生成（768P）', price: '1.2 点/秒（4s=6｜10s=12｜15s=18）', note: 'MiniMax H3 文生/图生/数字人' },
        { name: 'AI 视频生成（2K）', price: '1.73 点/秒（15s≈26）', note: '高清档' },
        { name: 'AI 视频 · 会员价', price: '进阶 9.5 折 / 高阶 9 折', note: '会员权益' },
        { name: 'AI 对话', price: '0.1 点/次（每日前 10 次免费）', note: '营销军师/助手' },
        { name: '文案洗稿', price: '1 点/次', note: 'DeepSeek 改写' },
        { name: '话术定制 / 营销军师', price: '1 点/次', note: '框架/文案/策略/角度' },
        { name: '线索采集 + 意图分析', price: '0.5 点/条', note: '弹幕/评论线索打标' },
        { name: '评论区互动', price: '0.1 点/次', note: '评论/点赞/关注' },
        { name: '爆款复刻 L1/L2/L3', price: '3 / 5 / 10 点/条', note: '抓取→洗稿→生成' },
        { name: 'STT 语音识别', price: '0.1 点/次', note: 'AI 助手语音输入' },
        { name: 'TTS 语音合成', price: '免费', note: '—' },
        { name: '数字名片', price: '免费', note: '获客钩子' },
        { name: '监控 / 截流任务', price: '免费', note: '按线索收费' }
      ]
    }
  }
}
</script>

<style scoped>
.help {
  padding: 20px;
}
.steps {
  line-height: 2;
  margin: 0;
  padding-left: 20px;
}
.steps li {
  margin-bottom: 4px;
}
.price-tips {
  margin-top: 10px;
  color: #8b949e;
  font-size: 14px;
  line-height: 1.8;
}
.price-tips p {
  margin: 0;
}
</style>
